import { debugLog } from './debug';

interface EnvVarConfig {
  name: string;
  required: boolean;
  defaultValue?: string;
  validate?: (value: string) => boolean;
  errorMessage?: string;
}

const requiredEnvVars: EnvVarConfig[] = [
  {
    name: 'VITE_API_MODULE1_URL',
    required: true,
    validate: (value) => value.startsWith('http'),
    errorMessage: 'Module 1 API URL must be a valid HTTP URL'
  },
  {
    name: 'VITE_API_MODULE2_URL',
    required: true,
    validate: (value) => value.startsWith('http'),
    errorMessage: 'Module 2 API URL must be a valid HTTP URL'
  },
  {
    name: 'VITE_API_MODULE3_URL',
    required: true,
    validate: (value) => value.startsWith('http'),
    errorMessage: 'Module 3 API URL must be a valid HTTP URL'
  },
  {
    name: 'VITE_API_MODULE4_URL',
    required: true,
    validate: (value) => value.startsWith('http'),
    errorMessage: 'Module 4 API URL must be a valid HTTP URL'
  },
  {
    name: 'VITE_AZURE_STORAGE_ACCOUNT_NAME',
    required: true
  },
  {
    name: 'VITE_AZURE_STORAGE_CONNECTION_STRING',
    required: true,
    validate: (value) => value.includes('DefaultEndpointsProtocol='),
    errorMessage: 'Invalid Azure Storage connection string format'
  }
];

const optionalEnvVars: EnvVarConfig[] = [
  {
    name: 'VITE_API_BASE_URL',
    required: false,
    defaultValue: 'http://localhost:8000',
    validate: (value) => value.startsWith('http'),
    errorMessage: 'Base API URL must be a valid HTTP URL'
  },
  {
    name: 'VITE_AUTH_ENABLED',
    required: false,
    defaultValue: 'false',
    validate: (value) => ['true', 'false'].includes(value.toLowerCase()),
    errorMessage: 'Auth enabled must be either "true" or "false"'
  },
  {
    name: 'VITE_DEBUG_MODE',
    required: false,
    defaultValue: 'false',
    validate: (value) => ['true', 'false'].includes(value.toLowerCase()),
    errorMessage: 'Debug mode must be either "true" or "false"'
  }
];

export class EnvironmentError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'EnvironmentError';
  }
}

export function validateEnvironment(): void {
  const errors: string[] = [];

  // Check required variables
  for (const config of requiredEnvVars) {
    const value = import.meta.env[config.name];
    
    if (!value && config.required) {
      errors.push(`Missing required environment variable: ${config.name}`);
      continue;
    }

    if (value && config.validate && !config.validate(value)) {
      errors.push(config.errorMessage || `Invalid value for ${config.name}`);
    }
  }

  // Check optional variables
  for (const config of optionalEnvVars) {
    const value = import.meta.env[config.name];
    
    if (value && config.validate && !config.validate(value)) {
      errors.push(config.errorMessage || `Invalid value for ${config.name}`);
    }
  }

  if (errors.length > 0) {
    const errorMessage = `Environment validation failed:\n${errors.join('\n')}`;
    debugLog('Environment', errorMessage);
    throw new EnvironmentError(errorMessage);
  }
}

export function getEnvVar(name: string, defaultValue?: string): string {
  const value = import.meta.env[name];
  
  if (!value && defaultValue === undefined) {
    throw new EnvironmentError(`Missing environment variable: ${name}`);
  }
  
  return value || defaultValue || '';
}

export function getEnvVarBoolean(name: string, defaultValue = false): boolean {
  const value = getEnvVar(name, defaultValue.toString());
  return value.toLowerCase() === 'true';
}

export function getEnvVarNumber(name: string, defaultValue?: number): number {
  const value = getEnvVar(name, defaultValue?.toString());
  const num = Number(value);
  
  if (isNaN(num)) {
    throw new EnvironmentError(`Invalid number for environment variable: ${name}`);
  }
  
  return num;
} 